import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LogOut, Moon, Sun, User, Mail, Calendar, Edit, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, token, setUser } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Push Notifications State
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [notificationsPermission, setNotificationsPermission] = useState('default');
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  // Check notification support and permission on mount
  useEffect(() => {
    const checkNotificationSupport = () => {
      const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
      setNotificationsSupported(supported);
      
      if (supported) {
        setNotificationsPermission(Notification.permission);
        // Check if user is subscribed
        checkSubscriptionStatus();
      }
    };
    
    checkNotificationSupport();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setNotificationsEnabled(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logout effettuato');
    navigate('/');
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      toast.error('Inserisci un nome valido');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        localStorage.setItem('fantaclasse_user', JSON.stringify(updatedUser));
        toast.success('Nome aggiornato con successo!');
        setShowEditDialog(false);
        setNewName('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setIsUpdating(false);
    }
  };

  const openEditDialog = () => {
    setNewName(user?.name || '');
    setShowEditDialog(true);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\\-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Enable push notifications
  const enableNotifications = async () => {
    if (!notificationsSupported) {
      toast.error('Le notifiche push non sono supportate dal tuo browser');
      return;
    }

    setIsLoadingNotifications(true);

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setNotificationsPermission(permission);

      if (permission !== 'granted') {
        toast.error('Permesso notifiche negato');
        setIsLoadingNotifications(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'BOjjju9SgM7--wWK9dzJm1fNpK-SY2j5G5CNSTezbES3wCYz9Z9YTuwQnLwuSjDpqG59jWsSpVqQ7cUX6_hfipk';

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
          }
        }),
      });

      if (response.ok) {
        setNotificationsEnabled(true);
        toast.success('Notifiche attivate con successo! 🔔');
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error("Errore durante l'attivazione delle notifiche");
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Disable push notifications
  const disableNotifications = async () => {
    setIsLoadingNotifications(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from push
        await subscription.unsubscribe();

        // Tell backend to remove subscription
        await fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setNotificationsEnabled(false);
        toast.success('Notifiche disattivate');
      }
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Errore durante la disattivazione');
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Test notification
  const sendTestNotification = async () => {
    try {
      const response = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Notifica di test inviata!');
      } else {
        toast.error("Errore durante l'invio della notifica di test");
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error("Errore durante l'invio");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="Profilo" />
      
      <main className="main-content p-4 max-w-md mx-auto">
        {/* Profile Card */}
        <Card className="mb-6" data-testid="profile-card">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Avatar className="w-24 h-24 mb-4">
                <AvatarImage src={user?.avatar_url} alt={user?.name} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <h2 
                className="text-xl font-bold mb-1"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="profile-name"
              >
                {user?.name}
              </h2>
              <p className="text-sm text-muted-foreground" data-testid="profile-email">
                {user?.email}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-base">Informazioni</CardTitle>
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={openEditDialog}
                  data-testid="edit-name-btn"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifica
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Modifica Nome</DialogTitle>
                  <DialogDescription>
                    Cambia il tuo nome utente. Questo nome sarà visibile agli altri membri delle tue leghe.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-name">Nuovo Nome</Label>
                    <Input
                      id="new-name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Inserisci il tuo nuovo nome"
                      disabled={isUpdating}
                      data-testid="new-name-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowEditDialog(false)}
                    disabled={isUpdating}
                  >
                    Annulla
                  </Button>
                  <Button 
                    onClick={handleUpdateName}
                    disabled={isUpdating || !newName.trim()}
                    data-testid="save-name-btn"
                  >
                    {isUpdating ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nome</p>
                <p className="font-medium">{user?.name}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Mail className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <Calendar className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Membro dal</p>
                <p className="font-medium">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Impostazioni</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  {isDark ? (
                    <Moon className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Sun className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <Label htmlFor="dark-mode" className="font-medium">Tema scuro</Label>
                  <p className="text-sm text-muted-foreground">
                    {isDark ? 'Attivo' : 'Disattivo'}
                  </p>
                </div>
              </div>
              <Switch
                id="dark-mode"
                checked={isDark}
                onCheckedChange={toggleTheme}
                data-testid="dark-mode-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifiche Push
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!notificationsSupported ? (
              <div className="text-sm text-muted-foreground p-4 bg-muted rounded-lg">
                ⚠️ Le notifiche push non sono supportate dal tuo browser o dispositivo.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      {notificationsEnabled ? (
                        <Bell className="w-5 h-5 text-cyan-500" />
                      ) : (
                        <BellOff className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <Label className="font-medium">
                        {notificationsEnabled ? 'Attive' : 'Disattivate'}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {notificationsEnabled 
                          ? 'Ricevi notifiche per giornate e scambi' 
                          : 'Attiva per ricevere aggiornamenti'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationsEnabled}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        enableNotifications();
                      } else {
                        disableNotifications();
                      }
                    }}
                    disabled={isLoadingNotifications || !notificationsSupported}
                    data-testid="notifications-switch"
                  />
                </div>

                {notificationsEnabled && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Riceverai notifiche per:</p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li>• 🏆 Giornata calcolata</li>
                        <li>• 🔄 Scambi proposti o accettati</li>
                        <li>• ⚡ Bonus/Malus aggiunti al tuo team</li>
                        <li>• 📊 Aggiornamenti importanti della lega</li>
                      </ul>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={sendTestNotification}
                      className="w-full"
                      data-testid="test-notification-btn"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Invia Notifica di Test
                    </Button>
                  </>
                )}

                {notificationsPermission === 'denied' && (
                  <div className="text-sm text-destructive p-3 bg-destructive/10 rounded-lg">
                    ⚠️ Le notifiche sono bloccate. Abilita le notifiche nelle impostazioni del browser.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button
          variant="destructive"
          className="w-full rounded-full"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Esci
        </Button>
      </main>

      <BottomNav />
    </div>
  );
};
